import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchBuchungenApi,
  createBuchungApi,
  updateBuchungApi,
  deleteBuchungApi,
  fetchDauerbuchungenApi,
  createDauerbuchungApi,
  updateDauerbuchungApi,
  deleteDauerbuchungApi,
  fetchKategorienApi,
  createKategorieApi,
  deleteKategorieApi,
} from './finanzenApi';
import { verschluessleJson, entschluessleJson } from 'core/features/sicherheit/crypto';
import { holeMasterKey } from 'core/features/sicherheit/vaultHolder';
import { sperren } from 'core/features/sicherheit/sicherheitSlice';
import { logout } from 'core/features/auth/authSlice';
import { TYP_AUSGABE, STANDARD_ZAHLWEG } from './konstanten';
import { signaturVon, findeDauerauftragFuer, passtZuDauerauftrag } from './dauerauftragErkennung';

/**
 * Redux-Slice fuer das Finanzen-Modul. Haelt die im Browser ENTSCHLUESSELTEN
 * Buchungen und Dauerauftraege (nur im Speicher). Der Master-Key liegt Portal-
 * weit im core-Feature "sicherheit". Alle Thunks ver-/entschluesseln an der
 * Grenze zur API; beim Sperren/Abmelden werden die Inhalte verworfen.
 *
 * Dauerauftraege ERZEUGEN keine Buchungen mehr, sondern buendeln erkannte
 * wiederkehrende Zahlungen: eine Buchung wird ueber `serienId` mit ihrem
 * Dauerauftrag verknuepft (beim Import automatisch, beim Bestaetigen eines
 * Vorschlags rueckwirkend) und kann so serienweit bearbeitet werden.
 */

// ── Buchungen ───────────────────────────────────────────

// Inhaltliche Felder einer Buchung, die verschluesselt abgelegt werden.
// serienId/periode verknuepfen aus Dauerauftraegen erzeugte Buchungen (sonst null).
// importId kennzeichnet aus einem Kontoauszug importierte Buchungen (Duplikat-Schutz).
const inhaltVon = (b) => ({
  typ: b.typ || TYP_AUSGABE,
  betrag: b.betrag != null ? Number(b.betrag) : 0,
  kategorie: b.kategorie || 'Sonstiges',
  zahlweg: b.zahlweg || STANDARD_ZAHLWEG,
  datum: b.datum || '',
  notiz: b.notiz || '',
  // Investitionsort (UUID eines Bereichs) – speist dessen Barvermoegen; null = nicht zugeordnet.
  ort: b.ort || null,
  serienId: b.serienId || null,
  periode: b.periode || null,
  importId: b.importId || null,
});

/** Entschluesselt ein Buchungs-DTO in ein Anzeige-Objekt. */
const entschluessleBuchung = async (mk, dto) => {
  const inhalt = await entschluessleJson(mk, dto.cipherData, dto.iv);
  return { uuid: dto.uuid, sortierung: dto.sortierung, createdAt: dto.createdAt, updatedAt: dto.updatedAt, ...inhaltVon(inhalt) };
};

/** Baut das verschluesselte Wire-DTO aus einem Anzeige-Objekt. */
const baueDto = async (mk, b) => {
  const blob = await verschluessleJson(mk, inhaltVon(b));
  return { cipherData: blob.cipherText, iv: blob.iv, sortierung: b.sortierung || 0 };
};

// ── Dauerauftraege (erkannte Serien wiederkehrender Buchungen) ──

// Inhaltliche Felder eines Dauerauftrags, die verschluesselt abgelegt werden.
// `signatur` = Empfaenger-Schluessel, ueber den Buchungen der Serie zugeordnet
// werden; `betrag` ist nur der repraesentative Anzeigewert (Median der Serie).
const dauerInhaltVon = (d) => ({
  typ: d.typ || TYP_AUSGABE,
  betrag: d.betrag != null ? Number(d.betrag) : 0,
  kategorie: d.kategorie || 'Sonstiges',
  zahlweg: d.zahlweg || STANDARD_ZAHLWEG,
  notiz: d.notiz || '',
  ort: d.ort || null,
  signatur: d.signatur || signaturVon(d.notiz),
  // Abgelehnter Vorschlag: bleibt gespeichert (kein erneuter Vorschlag), landet im Archiv.
  abgelehnt: d.abgelehnt === true,
});

/** Entschluesselt ein Dauerauftrags-DTO in ein Anzeige-Objekt. */
const entschluessleDauer = async (mk, dto) => {
  const inhalt = await entschluessleJson(mk, dto.cipherData, dto.iv);
  return { uuid: dto.uuid, sortierung: dto.sortierung, createdAt: dto.createdAt, updatedAt: dto.updatedAt, ...dauerInhaltVon(inhalt) };
};

/** Baut das verschluesselte Wire-DTO eines Dauerauftrags. */
const baueDauerDto = async (mk, d) => {
  const blob = await verschluessleJson(mk, dauerInhaltVon(d));
  return { cipherData: blob.cipherText, iv: blob.iv, sortierung: d.sortierung || 0 };
};

// ── Kategorien (benutzerdefiniert) ──────────────────────

// Inhaltliche Felder einer eigenen Kategorie, die verschluesselt abgelegt werden.
const kategorieInhaltVon = (k) => ({
  name: (k.name || '').trim(),
  icon: k.icon || '📦',
  typ: k.typ || TYP_AUSGABE,
});

/** Entschluesselt ein Kategorie-DTO in ein Anzeige-Objekt. */
const entschluessleKategorie = async (mk, dto) => {
  const inhalt = await entschluessleJson(mk, dto.cipherData, dto.iv);
  return { uuid: dto.uuid, sortierung: dto.sortierung, ...kategorieInhaltVon(inhalt) };
};

/** Baut das verschluesselte Wire-DTO einer eigenen Kategorie. */
const baueKategorieDto = async (mk, k) => {
  const blob = await verschluessleJson(mk, kategorieInhaltVon(k));
  return { cipherData: blob.cipherText, iv: blob.iv, sortierung: k.sortierung || 0 };
};

// ── Thunks ──────────────────────────────────────────────

/** Laedt Buchungen, Dauerauftraege und eigene Kategorien gemeinsam (beim Entsperren). */
export const ladeFinanzen = createAsyncThunk('finanzen/laden', async () => {
  const [buchungDtos, dauerDtos, katDtos] = await Promise.all([
    fetchBuchungenApi(),
    fetchDauerbuchungenApi(),
    fetchKategorienApi(),
  ]);
  const mk = holeMasterKey();
  const [buchungen, dauerbuchungen, kategorien] = await Promise.all([
    Promise.all((buchungDtos ?? []).map((d) => entschluessleBuchung(mk, d))),
    Promise.all((dauerDtos ?? []).map((d) => entschluessleDauer(mk, d))),
    Promise.all((katDtos ?? []).map((d) => entschluessleKategorie(mk, d))),
  ]);
  return { buchungen, dauerbuchungen, kategorien };
});

export const speichereBuchung = createAsyncThunk('finanzen/speichern', async (b) => {
  const mk = holeMasterKey();
  const dto = await baueDto(mk, b);
  const gespeichert = b.uuid ? await updateBuchungApi(b.uuid, dto) : await createBuchungApi(dto);
  return { uuid: gespeichert.uuid, sortierung: gespeichert.sortierung, createdAt: gespeichert.createdAt, updatedAt: gespeichert.updatedAt, ...inhaltVon(b) };
});

export const loescheBuchung = createAsyncThunk('finanzen/loeschen', async (uuid) => deleteBuchungApi(uuid));

// Groesse der parallelen Buendel bei Massen-Operationen auf Buchungen.
const BUENDEL = 8;

/**
 * Loescht alle NICHT einem Dauerauftrag zugeordneten Buchungen (ohne serienId) –
 * also manuell erfasste und noch nicht verknuepfte importierte. Verknuepfte
 * Serien-Buchungen bleiben. Die Unterscheidung ist nur im Browser moeglich
 * (serienId liegt im Ciphertext). Liefert die entfernten UUIDs zurueck.
 */
export const loescheNichtSerienBuchungen = createAsyncThunk(
  'finanzen/loescheNichtSerien',
  async (_, { getState }) => {
    const ziel = getState().finanzen.buchungen.filter((b) => !b.serienId);
    const geloescht = [];
    for (let i = 0; i < ziel.length; i += BUENDEL) {
      const teil = ziel.slice(i, i + BUENDEL);
      await Promise.all(teil.map((b) => deleteBuchungApi(b.uuid)));
      geloescht.push(...teil.map((b) => b.uuid));
    }
    return geloescht;
  }
);

/** Aktualisiert Buchungen gebuendelt (re-verschluesselt) und liefert die neuen Anzeige-Objekte. */
const aktualisiereBuchungenGebuendelt = async (mk, buchungen) => {
  const ergebnis = [];
  for (let i = 0; i < buchungen.length; i += BUENDEL) {
    const teil = buchungen.slice(i, i + BUENDEL);
    const teilErgebnis = await Promise.all(
      teil.map(async (b) => {
        const g = await updateBuchungApi(b.uuid, await baueDto(mk, b));
        return { uuid: g.uuid, sortierung: g.sortierung, createdAt: g.createdAt, updatedAt: g.updatedAt, ...inhaltVon(b) };
      })
    );
    ergebnis.push(...teilErgebnis);
  }
  return ergebnis;
};

/** Baut den Anzeige-Datensatz eines gespeicherten Dauerauftrags aus der Server-Antwort. */
const baueDauerauftrag = (gespeichert, inhalt) => ({
  uuid: gespeichert.uuid, sortierung: gespeichert.sortierung, createdAt: gespeichert.createdAt, updatedAt: gespeichert.updatedAt, ...dauerInhaltVon(inhalt),
});

/** Verknuepft alle noch freien, passenden Buchungen mit einem Dauerauftrag (serienId + dessen Kategorie). */
const verknuepfePassendeBuchungen = async (mk, buchungen, dauerauftrag) => {
  const passend = buchungen.filter((b) => !b.serienId && passtZuDauerauftrag(b, dauerauftrag));
  return aktualisiereBuchungenGebuendelt(
    mk,
    passend.map((b) => ({ ...b, serienId: dauerauftrag.uuid, kategorie: dauerauftrag.kategorie }))
  );
};

/** Uebernimmt die Vorschlags-Felder in einen Dauerauftrags-Datensatz. */
const gruppeAusVorschlag = (vorschlag, abgelehnt) => ({
  typ: vorschlag.typ,
  betrag: vorschlag.betrag,
  kategorie: vorschlag.kategorie,
  zahlweg: vorschlag.zahlweg,
  notiz: vorschlag.notiz,
  ort: vorschlag.ort || null,
  signatur: vorschlag.signatur,
  abgelehnt,
});

/**
 * Bestaetigt einen erkannten Vorschlag: legt den Dauerauftrag (Serie) an und
 * verknuepft alle passenden vorhandenen Buchungen rueckwirkend (serienId +
 * vereinheitlichte Kategorie).
 */
export const bestaetigeDauerauftrag = createAsyncThunk(
  'finanzen/dauerBestaetigen',
  async (vorschlag, { getState }) => {
    const mk = holeMasterKey();
    const gruppe = gruppeAusVorschlag(vorschlag, false);
    const gespeichert = await createDauerbuchungApi(await baueDauerDto(mk, gruppe));
    const dauerauftrag = baueDauerauftrag(gespeichert, gruppe);
    const aktualisiert = await verknuepfePassendeBuchungen(mk, getState().finanzen.buchungen, dauerauftrag);
    return { dauerauftrag, aktualisiert };
  }
);

/** Lehnt einen Vorschlag ab: speichert ihn (Archiv), ohne Buchungen zu verknuepfen. */
export const lehneVorschlagAb = createAsyncThunk('finanzen/dauerAblehnen', async (vorschlag) => {
  const mk = holeMasterKey();
  const gruppe = gruppeAusVorschlag(vorschlag, true);
  const gespeichert = await createDauerbuchungApi(await baueDauerDto(mk, gruppe));
  return { dauerauftrag: baueDauerauftrag(gespeichert, gruppe), aktualisiert: [] };
});

/** Reaktiviert einen abgelehnten Dauerauftrag aus dem Archiv und verknuepft passende Buchungen. */
export const reaktiviereDauerauftrag = createAsyncThunk(
  'finanzen/dauerReaktivieren',
  async (uuid, { getState }) => {
    const mk = holeMasterKey();
    const { buchungen, dauerbuchungen } = getState().finanzen;
    const gruppe = { ...dauerbuchungen.find((d) => d.uuid === uuid), abgelehnt: false };
    const gespeichert = await updateDauerbuchungApi(uuid, await baueDauerDto(mk, gruppe));
    const dauerauftrag = baueDauerauftrag(gespeichert, gruppe);
    const aktualisiert = await verknuepfePassendeBuchungen(mk, buchungen, dauerauftrag);
    return { dauerauftrag, aktualisiert };
  }
);

/**
 * Bearbeitet eine ganze Serie: uebernimmt die geaenderten Felder (Kategorie,
 * Notiz, Zahlweg, Ort) in den Dauerauftrag UND in alle verknuepften Buchungen.
 * Die `signatur` (Verknuepfungsschluessel) bleibt unveraendert.
 */
export const bearbeiteSerie = createAsyncThunk(
  'finanzen/serieBearbeiten',
  async ({ uuid, aenderungen }, { getState }) => {
    const mk = holeMasterKey();
    const { buchungen, dauerbuchungen } = getState().finanzen;
    const alt = dauerbuchungen.find((d) => d.uuid === uuid);
    const neueGruppe = { ...alt, ...aenderungen };
    const gespeichert = await updateDauerbuchungApi(uuid, await baueDauerDto(mk, neueGruppe));
    const dauerauftrag = baueDauerauftrag(gespeichert, neueGruppe);
    const aktualisiert = await aktualisiereBuchungenGebuendelt(
      mk,
      buchungen.filter((b) => b.serienId === uuid).map((b) => ({ ...b, ...aenderungen }))
    );
    return { dauerauftrag, aktualisiert };
  }
);

/** Loest eine Serie auf: entfernt die Verknuepfung (serienId) aller Buchungen und loescht den Dauerauftrag. */
export const loeseDauerauftragAuf = createAsyncThunk(
  'finanzen/dauerAufloesen',
  async (uuid, { getState }) => {
    const mk = holeMasterKey();
    const aktualisiert = await aktualisiereBuchungenGebuendelt(
      mk,
      getState().finanzen.buchungen.filter((b) => b.serienId === uuid).map((b) => ({ ...b, serienId: null }))
    );
    await deleteDauerbuchungApi(uuid);
    return { uuid, aktualisiert };
  }
);

export const speichereKategorie = createAsyncThunk('finanzen/katSpeichern', async (k) => {
  const mk = holeMasterKey();
  const dto = await baueKategorieDto(mk, k);
  const gespeichert = await createKategorieApi(dto);
  return { uuid: gespeichert.uuid, sortierung: gespeichert.sortierung, ...kategorieInhaltVon(k) };
});

export const loescheKategorie = createAsyncThunk('finanzen/katLoeschen', async (uuid) =>
  deleteKategorieApi(uuid)
);

/**
 * Importiert vorbereitete Buchungen aus einem Kontoauszug (oder aus Kontowecker-
 * Mails): ueberspringt bereits vorhandene (per importId), verknuepft passende
 * Buchungen automatisch mit einem gespeicherten Dauerauftrag (serienId +
 * dessen Kategorie), verschluesselt und speichert die neuen in kleinen parallelen
 * Buendeln. Meldet den Fortschritt fuer die Anzeige zurueck.
 */
export const importiereBuchungen = createAsyncThunk(
  'finanzen/importieren',
  async ({ zeilen }, { getState, dispatch }) => {
    const { buchungen, dauerbuchungen } = getState().finanzen;
    const vorhandene = new Set(buchungen.filter((b) => b.importId).map((b) => b.importId));
    const neu = zeilen
      .filter((z) => !z.importId || !vorhandene.has(z.importId))
      .map((z) => verknuepfeMitDauerauftrag(z, dauerbuchungen));
    dispatch(importGestartet(neu.length));
    const mk = holeMasterKey();
    const erstellt = [];
    for (let i = 0; i < neu.length; i += BUENDEL) {
      const teil = neu.slice(i, i + BUENDEL);
      const ergebnisse = await Promise.all(
        teil.map(async (b) => {
          const gespeichert = await createBuchungApi(await baueDto(mk, b));
          return { uuid: gespeichert.uuid, sortierung: gespeichert.sortierung, createdAt: gespeichert.createdAt, updatedAt: gespeichert.updatedAt, ...inhaltVon(b) };
        })
      );
      erstellt.push(...ergebnisse);
      dispatch(importFortschritt(erstellt.length));
    }
    return { erstellt, uebersprungen: zeilen.length - neu.length };
  }
);

/** Haengt eine Import-Buchung an einen passenden Dauerauftrag (serienId + dessen Kategorie). */
const verknuepfeMitDauerauftrag = (buchung, dauerbuchungen) => {
  const dauerauftrag = findeDauerauftragFuer(buchung, dauerbuchungen);
  return dauerauftrag
    ? { ...buchung, serienId: dauerauftrag.uuid, kategorie: dauerauftrag.kategorie }
    : buchung;
};

const initialState = {
  buchungen: [],
  dauerbuchungen: [],
  kategorien: [],
  status: 'idle',
  error: null,
  import: { laeuft: false, fertig: 0, gesamt: 0, uebersprungen: 0, fehler: null },
};

/** Verwirft alle entschluesselten Inhalte (beim Sperren/Abmelden). */
const leereInhalte = (state) => {
  state.buchungen = [];
  state.dauerbuchungen = [];
  state.kategorien = [];
  state.status = 'idle';
  state.error = null;
  state.import = { laeuft: false, fertig: 0, gesamt: 0, uebersprungen: 0, fehler: null };
};

/** Uebernimmt das Ergebnis einer Serien-Operation (Dauerauftrag + geaenderte Buchungen). */
const uebernehmeSerienErgebnis = (state, { dauerauftrag, aktualisiert }) => {
  upsert(state.dauerbuchungen, dauerauftrag);
  aktualisiert.forEach((b) => upsert(state.buchungen, b));
};

/** Ersetzt ein Element (per uuid) in der Liste oder haengt es an. */
const upsert = (liste, element) => {
  const idx = liste.findIndex((e) => e.uuid === element.uuid);
  if (idx >= 0) liste[idx] = element;
  else liste.push(element);
};

const finanzenSlice = createSlice({
  name: 'finanzen',
  initialState,
  reducers: {
    // Fortschritts-Steuerung des Kontoauszug-Imports (aus dem Import-Thunk dispatcht).
    importGestartet: (state, action) => {
      state.import = { laeuft: true, fertig: 0, gesamt: action.payload, uebersprungen: 0, fehler: null };
    },
    importFortschritt: (state, action) => {
      state.import.fertig = action.payload;
    },
    importZuruecksetzen: (state) => {
      state.import = { laeuft: false, fertig: 0, gesamt: 0, uebersprungen: 0, fehler: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sperren, leereInhalte)
      .addCase(logout, leereInhalte)
      .addCase(ladeFinanzen.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(ladeFinanzen.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.buchungen = action.payload.buchungen;
        state.dauerbuchungen = action.payload.dauerbuchungen;
        state.kategorien = action.payload.kategorien;
      })
      .addCase(ladeFinanzen.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(speichereBuchung.fulfilled, (state, action) => {
        upsert(state.buchungen, action.payload);
      })
      .addCase(loescheBuchung.fulfilled, (state, action) => {
        state.buchungen = state.buchungen.filter((b) => b.uuid !== action.payload);
      })
      .addCase(loescheNichtSerienBuchungen.fulfilled, (state, action) => {
        const weg = new Set(action.payload);
        state.buchungen = state.buchungen.filter((b) => !weg.has(b.uuid));
      })
      .addCase(bestaetigeDauerauftrag.fulfilled, (state, action) => {
        uebernehmeSerienErgebnis(state, action.payload);
      })
      .addCase(lehneVorschlagAb.fulfilled, (state, action) => {
        uebernehmeSerienErgebnis(state, action.payload);
      })
      .addCase(reaktiviereDauerauftrag.fulfilled, (state, action) => {
        uebernehmeSerienErgebnis(state, action.payload);
      })
      .addCase(bearbeiteSerie.fulfilled, (state, action) => {
        uebernehmeSerienErgebnis(state, action.payload);
      })
      .addCase(loeseDauerauftragAuf.fulfilled, (state, action) => {
        state.dauerbuchungen = state.dauerbuchungen.filter((d) => d.uuid !== action.payload.uuid);
        action.payload.aktualisiert.forEach((b) => upsert(state.buchungen, b));
      })
      .addCase(speichereKategorie.fulfilled, (state, action) => {
        upsert(state.kategorien, action.payload);
      })
      .addCase(loescheKategorie.fulfilled, (state, action) => {
        state.kategorien = state.kategorien.filter((k) => k.uuid !== action.payload);
      })
      .addCase(importiereBuchungen.fulfilled, (state, action) => {
        action.payload.erstellt.forEach((b) => upsert(state.buchungen, b));
        state.import.laeuft = false;
        state.import.uebersprungen = action.payload.uebersprungen;
      })
      .addCase(importiereBuchungen.rejected, (state, action) => {
        state.import.laeuft = false;
        state.import.fehler = action.error.message;
      });
  },
});

export const { importGestartet, importFortschritt, importZuruecksetzen } = finanzenSlice.actions;

export const selectBuchungen = (state) => state.finanzen.buchungen;
export const selectBuchungenStatus = (state) => state.finanzen.status;
export const selectDauerbuchungen = (state) => state.finanzen.dauerbuchungen;
export const selectKategorien = (state) => state.finanzen.kategorien;
export const selectImportStatus = (state) => state.finanzen.import;

export default finanzenSlice.reducer;
